// Report-type slugs the backend emits (e.g. /strategy/108) and that we
// also accept under /reports/{id}/{type}. Both forms resolve to the
// canonical /reports/{name}?report={type} URL.
const REPORT_TYPE_SLUGS = [
  'strategy',
  'credit-paper',
  'fx-exposure',
  'ai-exposure',
  'people-and-org',
  'treasury-ops',
  'ops-supply-chain',
  'business-overview',
  'financial-report',
  'airline-fuel-analysis',
];

import companyService from '@/api/services/company';

async function resolveCompanyName(idOrName) {
  if (!/^\d+$/.test(idOrName)) return decodeURIComponent(idOrName);
  const id = Number(idOrName);
  const result = await companyService.getAllManagedCompanies();
  const match = (result.companies || []).find((c) => c.id === id);
  return match?.displayName || null;
}

export default defineNuxtRouteMiddleware(async (to) => {
  const { hasToken, checkAuth } = useAuth();

  await checkAuth();

  // If user is authenticated, prevent visiting auth pages (except password reset flow)
  const passwordResetPages = ['/auth/forget-password', '/auth/password-reset'];
  if (to.path.startsWith('/auth') && hasToken.value && !passwordResetPages.includes(to.path)) {
    return navigateTo('/');
  }

  // The living style guide renders tokens and nothing else — no API call, no
  // customer data — and a design system you cannot open without a session is
  // one nobody checks their work against. Open in LOCAL DEV ONLY; in every
  // deployed environment it needs a session like any other page.
  const isStyleGuide = to.path === '/design-system';
  if (isStyleGuide && import.meta.dev) return;

  // If user is unauthenticated, require signin for non-auth pages
  if (!to.path.startsWith('/auth') && !hasToken.value) {
    return navigateTo('/auth/signin');
  }

  // /strategy/108 → /reports/108/strategy. Cheap to do first since it's just
  // a string rewrite; the heavier lookup happens in the next block.
  const bareMatch = to.path.match(/^\/([^/]+)\/(\d+)\/?$/);
  if (bareMatch && REPORT_TYPE_SLUGS.includes(bareMatch[1])) {
    return navigateTo(`/reports/${bareMatch[2]}/${bareMatch[1]}`, { replace: true });
  }

  // /reports/{id-or-name}/{type} → look up name (when numeric) and forward
  // to the existing /reports/{name}?report={type} page. Done in middleware
  // because the sibling pages/reports/[name].vue + [name]/[type].vue layout
  // would otherwise treat [name].vue as a parent route without a NuxtPage
  // slot, swallowing the child render.
  const reportsMatch = to.path.match(/^\/reports\/([^/]+)\/([^/]+)\/?$/);
  if (reportsMatch) {
    const [, idOrName, type] = reportsMatch;
    try {
      const name = await resolveCompanyName(idOrName);
      if (name) {
        return navigateTo(
          { path: `/reports/${encodeURIComponent(name)}`, query: { report: type } },
          { replace: true }
        );
      }
    } catch (e) {
      console.error('[reports middleware] company lookup failed:', e);
    }
  }
});
